import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, RotateCcw } from "lucide-react";
import { getOwnerRecord } from "../../../api/collingsData";
import { KIND_COLORS, KIND_LABELS } from "../components/PropertyMapView";
import { OwnerContactList } from "../../dashboard/components/RelistingOwnerContacts";

/**
 * One address from the office's spreadsheets (`/collings-data-properties/owner/:key`),
 * whether or not Homer ever listed it. Christian Gravias, Monday 13354971743
 * (26 Oct 2026): "I have searched some of these addresses but they arent
 * coming up as a profile in our CRM? Even if the properties arent withdrawn
 * can we attach the data we have to the public property files regardless?"
 * The "On file" tab of the Public Property Search and the dashboard search
 * bar land here. Shows everyone on file for the address, its Homer campaigns
 * when there are any, and its Re-listing row.
 */

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

const OwnerRecordPage = () => {
    const { key } = useParams();
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setRecord(null);
        setError(null);
        getOwnerRecord(key)
            .then((res) => !cancelled && setRecord(res.data))
            .catch((err) => {
                if (cancelled) return;
                setError(err.response?.status === 404 ? "This address is no longer in our records." : "Could not load this address.");
            });
        return () => {
            cancelled = true;
        };
    }, [key]);

    const back = (
        <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#55C5D0]"
        >
            <ArrowLeft className="h-4 w-4" /> Back
        </button>
    );

    if (error) {
        return (
            <div className="mx-auto max-w-5xl space-y-4 p-4">
                {back}
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">{error}</div>
            </div>
        );
    }
    if (!record) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-[#55C5D0]" />
            </div>
        );
    }

    const homer = record.homer || [];
    const relisting = record.relisting;

    return (
        <div className="mx-auto max-w-6xl space-y-5 p-2 sm:p-4">
            {back}

            <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-teal-700 px-2.5 py-0.5 text-xs font-bold text-white">On file</span>
                    <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                        Public Property
                    </span>
                </div>
                <h1 className="mt-2 text-xl sm:text-2xl font-bold text-gray-900">{record.address}</h1>
                <p className="mt-1 text-sm text-gray-500">
                    {homer.length
                        ? `${homer.length} Homer ${homer.length === 1 ? "campaign" : "campaigns"} at this address.`
                        : "Homer has never listed this address. The details below come from our own records."}
                </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-5">
                    <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-5">
                        <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                            Owner details <span className="font-semibold normal-case tracking-normal text-gray-400">· from our records</span>
                        </h2>
                        <OwnerContactList contacts={record.owner_contacts || []} />
                    </div>

                    {relisting ? (
                        <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-5">
                            <h2 className="mb-1 text-sm font-bold uppercase tracking-wide text-gray-500">Re-listing Opportunities</h2>
                            <p className="text-sm text-gray-700">
                                {relisting.withdrawn_date ? `Withdrawn ${fmtDate(relisting.withdrawn_date)}. ` : ""}
                                This property is on the Re-listing list.
                            </p>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`/prospecting/relisting-opportunities?q=${encodeURIComponent(relisting.address.split(",")[0])}`)
                                }
                                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-orange-700 ring-1 ring-orange-200 hover:bg-orange-100"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Open on Re-listing
                            </button>
                        </div>
                    ) : null}

                    {homer.length ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-5">
                            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Homer campaigns</h2>
                            {homer.map((h) => (
                                <button
                                    key={h.id}
                                    type="button"
                                    onClick={() => navigate(`/collings-data-properties/homer/${h.id}`)}
                                    className="flex w-full items-center justify-between gap-3 border-b border-gray-100 py-2 text-left text-sm last:border-0 hover:text-[#2b8e97]"
                                >
                                    <span className="flex items-center gap-2">
                                        <span
                                            className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                                            style={{ backgroundColor: KIND_COLORS[h.kind] || KIND_COLORS.not_for_sale }}
                                        >
                                            {KIND_LABELS[h.kind] || h.kind_label}
                                        </span>
                                        {h.price_display}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {fmtDate(h.sold_date || h.withdrawn_date || h.listed_date)}
                                        {h.agency_name ? ` · ${h.agency_name}` : ""}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <iframe
                        title="Property location"
                        className="h-80 w-full lg:h-full lg:min-h-[420px]"
                        loading="lazy"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(record.address)}&z=16&output=embed`}
                    />
                </div>
            </div>
        </div>
    );
};

export default OwnerRecordPage;
